import {
  MIN_SECTION_WIDTH_FOR_LABELS,
  MIN_SECTION_WIDTH_FOR_TOTALS,
} from 'modules/experiments/constants';
import { stylishMetricResult, suffixNumberDisplay } from 'modules/experiments/utils';
import { colors } from 'ui/theme';
import { AudienceBreakdownMetric, AudienceBreakdownSection } from 'modules/experiments/types';

import { AudienceBreakdownTooltip } from './AudienceBreakdownTooltip';
import {
  ChartSection,
  StyledTopTooltip,
  TopBlockGroup,
  TopBlock,
  BottomBlock,
  GroupLabel,
  GroupValues,
  PercentValue,
  TotalValue,
} from './Styled';

type AudienceBreakdownChartSectionProps = {
  chartData: AudienceBreakdownMetric;
  index: number;
  isFirstSection: boolean;
  isLastSection: boolean;
  hasSendSize: boolean;
};

export const AudienceBreakdownChartSection = ({
  chartData,
  index,
  isFirstSection,
  isLastSection,
  hasSendSize,
}: AudienceBreakdownChartSectionProps) => {
  const { percent, subsections, label, total } = chartData;
  const isVariantSection = chartData.label === AudienceBreakdownSection.TEST;
  const isVariantPercentBelowTwo = percent < subsections.length * 2;
  // leaving isTooltipEnabled as a variable in case we want to add additional tooltip requirements in design review
  const isTooltipEnabled = true;

  return (
    <ChartSection
      percent={percent}
      key={index}
      isTooltipEnabled={isTooltipEnabled}
      data-test={`audience-breakdown-chart-section-${label}`}
      data-percentage={`${percent}%`}
    >
      <StyledTopTooltip
        isEnabled={isTooltipEnabled}
        tooltipContent={
          <AudienceBreakdownTooltip
            label={label}
            percent={percent}
            total={total}
            hasSendSize={hasSendSize}
            isVariantSection={isVariantSection}
            variants={subsections.map(
              ({ alphabet, backgroundColor, sends, percent: variantPercent, borderColor }) => ({
                alphabet,
                backgroundColor,
                variantPercent,
                sends,
                borderColor,
              }),
            )}
          />
        }
      >
        <TopBlockGroup data-test="charts-top-block">
          {/* condenses test variants to single block 
              if variant gets below 2% */}
          {isVariantPercentBelowTwo ? (
            <TopBlock
              key={`top-block-singular`}
              color={subsections[0].color}
              backgroundColor={
                isVariantSection ? colors.purple50 : subsections?.[0].backgroundColor
              }
              borderColor={isVariantSection ? colors.purple100 : subsections?.[0].borderColor}
              first={isFirstSection}
              last={isLastSection}
            ></TopBlock>
          ) : (
            subsections.map((subsection, i) => {
              const firstSubsection = i === 0;
              const lastSubsection = i === subsections.length - 1;
              const first = isFirstSection && firstSubsection;
              const last = isLastSection && lastSubsection;
              return (
                <TopBlock
                  key={`top-block-${i}`}
                  color={subsection.color}
                  backgroundColor={subsection.backgroundColor}
                  borderColor={subsection.borderColor}
                  first={first}
                  last={last}
                >
                  {subsection.alphabet}
                </TopBlock>
              );
            })
          )}
        </TopBlockGroup>
        <BottomBlock first={isFirstSection} last={isLastSection} percent={percent}>
          {percent < MIN_SECTION_WIDTH_FOR_LABELS ? (
            <></>
          ) : (
            <>
              <GroupLabel data-test={'chart-section-label'}>{label}</GroupLabel>
              <GroupValues>
                <PercentValue>{stylishMetricResult(percent / 100, 'percentage')}</PercentValue>
                {percent < MIN_SECTION_WIDTH_FOR_TOTALS || !hasSendSize ? null : (
                  <TotalValue>~{suffixNumberDisplay(total)}</TotalValue>
                )}
              </GroupValues>
            </>
          )}
        </BottomBlock>
      </StyledTopTooltip>
    </ChartSection>
  );
};
